import {
	FaFileInvoiceDollar,
	FaWallet,
	FaBuilding,
	FaTags,
	FaKey,
	FaMoneyCheckAlt,
	FaCalendarAlt,
	FaListAlt,
	FaHistory,
	FaBalanceScale,
} from 'react-icons/fa';

/**
 * Single source of truth for the report catalog: card metadata (group,
 * module color, icon), the FilterForm formType it drives, and the exact
 * filter defaults/URL keys it owns. `id` values remain stable so saved report
 * links and the background report actions continue to resolve correctly.
 */

export const REPORT_GROUPS = [
	{ id: 'area', title: 'Por área operativa', hint: 'listados completos' },
	{ id: 'dues', title: 'Cuotas y vencimientos', hint: 'por período' },
	{ id: 'consol', title: 'Consolidado', hint: 'por cuenta' },
];

// Tailwind's content scanner needs literal class strings, never `tw:bg-${x}`.
export const MODULE_CLASSES = {
	'cat-1': { tile: 'tw:bg-cat-1', border: 'tw:border-cat-1', check: 'tw:border-cat-1 tw:bg-cat-1' },
	'cat-2': { tile: 'tw:bg-cat-2', border: 'tw:border-cat-2', check: 'tw:border-cat-2 tw:bg-cat-2' },
	'cat-3': { tile: 'tw:bg-cat-3', border: 'tw:border-cat-3', check: 'tw:border-cat-3 tw:bg-cat-3' },
	'cat-4': { tile: 'tw:bg-cat-4', border: 'tw:border-cat-4', check: 'tw:border-cat-4 tw:bg-cat-4' },
	'cat-5': { tile: 'tw:bg-cat-5', border: 'tw:border-cat-5', check: 'tw:border-cat-5 tw:bg-cat-5' },
	'ok': { tile: 'tw:bg-ok', border: 'tw:border-ok', check: 'tw:border-ok tw:bg-ok' },
	'accent': { tile: 'tw:bg-accent', border: 'tw:border-accent', check: 'tw:border-accent tw:bg-accent' },
};

export const REPORTS = [
	{
		id: 'Deudores',
		title: 'Deudores',
		subtitle: 'Deudas registradas, capital y cuotas',
		description: 'Este reporte permite traer todas las deudas registradas en el sistema. Se puede filtrar por concepto, por la fecha de origen y la fecha de vencimiento dentro de un rango de fechas, por un rango de monto de cuota, por un rango de capital y para una moneda específica. Lista información del deudor, el concepto, el capital, la cantidad de cuotas, el monto de cada cuota, la periodicidad, las fechas de origen y vencimiento y la fecha del último pago.',
		group: 'area',
		module: 'cat-1',
		Icon: FaFileInvoiceDollar,
		formType: 'Deudas',
		hasFilters: true,
		defaults: {
			concept: 'all', dateFrom: '', dateTo: '', endDateFrom: '', endDateTo: '',
			amountFrom: 0, amountTo: '', capitalFrom: '', capitalTo: '', currency: 'all', query: 'all',
		},
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'endDateFrom', 'endDateTo', 'amountFrom', 'amountTo', 'capitalFrom', 'capitalTo', 'currency', 'query'],
	},
	{
		id: 'Pasividades',
		title: 'Pasividades',
		subtitle: 'Créditos a pagar y su capital',
		description: 'Este reporte permite traer todas las pasividades (créditos a pagar) registradas en el sistema. Se puede filtrar por concepto, por periodicidad, por la fecha de origen y la fecha de vencimiento dentro de un rango de fechas, por un rango de monto de cuota y para una moneda específica. Lista información del acreedor, el concepto, el capital, la cantidad de cuotas, el monto de cada cuota, la periodicidad y el total garantizado.',
		group: 'area',
		module: 'cat-5',
		Icon: FaWallet,
		formType: 'Pasividades',
		hasFilters: true,
		defaults: {
			concept: 'all', dateFrom: '', dateTo: '', endDateFrom: '', endDateTo: '',
			amountFrom: 0, amountTo: '', capitalFrom: '', capitalTo: '', currency: 'all', query: 'all',
		},
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'endDateFrom', 'endDateTo', 'amountFrom', 'amountTo', 'capitalFrom', 'capitalTo', 'currency', 'query'],
	},
	{
		id: 'Activos Fijos',
		title: 'Activos Fijos',
		subtitle: 'Activos, propietarios y padrones',
		description: 'Este reporte permite traer todos los activos fijos registrados en el sistema. Se puede filtrar por un rango de monto y para una moneda específica. Lista información del activo, su propietario, el precio o valor, el tipo, la ubicación, la descripción, el monto garantizado y los padrones asociados.',
		group: 'area',
		module: 'cat-4',
		Icon: FaBuilding,
		formType: 'ActivosFijos',
		hasFilters: true,
		defaults: {
			currency: 'all', priceFrom: 0, priceTo: '', name: '', owner: '', sold: '', ubication: '', lot: '',
		},
		urlKeys: ['owner', 'priceFrom', 'priceTo', 'currency', 'sold', 'name', 'ubication', 'lot'],
	},
	{
		id: 'Ventas',
		title: 'Ventas',
		subtitle: 'Ventas de activos y compradores',
		description: 'Este reporte permite traer todas las ventas de activos realizadas en el sistema. Se puede filtrar por la fecha de venta dentro de un rango de fechas, por un rango de monto y para una moneda específica. Lista información del comprador, la propiedad y el padrón, la fecha, el monto, el total de deudas y el pago inicial.',
		group: 'area',
		module: 'cat-3',
		Icon: FaTags,
		formType: 'VentasActivo',
		hasFilters: true,
		defaults: { dateFrom: '', dateTo: '', currency: 'all', lot: '', purchaser: 'all', query: 'all' },
		urlKeys: ['dateFrom', 'dateTo', 'currency', 'lot', 'purchaser', 'query'],
	},
	{
		id: 'Alquileres',
		title: 'Alquileres',
		subtitle: 'Contratos de alquiler vigentes',
		description: 'Este reporte permite traer todos los contratos de alquiler registrados en el sistema. Se puede filtrar por el período de inicio y el período de fin dentro de un rango de fechas, por un rango de monto y para una moneda específica. Lista información del inquilino, el monto, las fechas de inicio y fin, la carpeta, el saldo anterior y los datos del último ajuste.',
		group: 'area',
		module: 'cat-2',
		Icon: FaKey,
		formType: 'Alquileres',
		hasFilters: true,
		defaults: {
			periodInitFrom: '', periodInitTo: '', periodEndFrom: '', periodEndTo: '',
			amountFrom: 0, amountTo: '', currency: 'all', query: 'all',
		},
		urlKeys: ['periodInitFrom', 'periodInitTo', 'periodEndFrom', 'periodEndTo', 'amountFrom', 'amountTo', 'currency', 'query'],
	},
	{
		id: 'Cobros',
		title: 'Cobros',
		subtitle: 'Cuotas cobradas por concepto',
		description: 'Este reporte permite traer todas las cuotas que registran cobros en el sistema bajo un concepto específico. Se puede filtrar por la fecha de cobro dentro de un rango de fechas o para una moneda específica. Lista información del deudor, la cuota de la deuda y los montos adeudados y cobrados.',
		group: 'area',
		module: 'ok',
		Icon: FaMoneyCheckAlt,
		formType: 'Cobros',
		hasFilters: true,
		defaults: { concept: 'all', dateFrom: '', dateTo: '', currency: 'all' },
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'currency'],
	},
	{
		id: 'Cuotas de deudas a cobrar mes actual',
		title: 'Cuotas de deudas — mes actual',
		subtitle: 'Cuotas pendientes del mes en curso',
		description: 'Este reporte permite traer todas las cuotas de deudas cuyo vencimiento cae dentro del mes en curso y que están pendientes de cobro. No requiere filtros: se genera directamente para el mes actual. Lista información del deudor, el concepto, la cuota, la amortización, el interés, el recargo, el monto, el saldo, el monto pendiente, el estado de pago y la fecha del último pago.',
		group: 'dues',
		module: 'cat-1',
		Icon: FaCalendarAlt,
		formType: 'CuotasDeudas',
		hasFilters: false,
		defaults: { concept: 'all', dateFrom: '', dateTo: '', currency: 'all', lastPaymentFrom: '', lastPaymentTo: '', query: 'all' },
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'currency', 'query', 'lastPaymentFrom', 'lastPaymentTo'],
	},
	{
		id: 'Todas las cuotas de deudas',
		title: 'Todas las cuotas de deudas',
		subtitle: 'Historial completo de cuotas',
		description: 'Este reporte permite traer todas las cuotas de deudas generadas en el sistema. Se puede filtrar por concepto, por estado de pago, por la fecha dentro de un rango de fechas y para una moneda específica. Lista información del deudor, el concepto, la cuota, la amortización, el interés, el recargo, el monto, el saldo, el monto pendiente, el estado de pago y la fecha del último pago.',
		group: 'dues',
		module: 'cat-1',
		Icon: FaListAlt,
		formType: 'CuotasDeudas',
		hasFilters: true,
		defaults: { concept: 'all', dateFrom: '', dateTo: '', currency: 'all', lastPaymentFrom: '', lastPaymentTo: '', query: 'all' },
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'currency', 'query', 'lastPaymentFrom', 'lastPaymentTo'],
	},
	{
		id: 'Cuotas de pasividades a pagar mes actual',
		title: 'Cuotas de pasividades — mes actual',
		subtitle: 'Cuotas a pagar del mes en curso',
		description: 'Este reporte permite traer todas las cuotas de pasividades cuyo vencimiento cae dentro del mes en curso y que están pendientes de pago. No requiere filtros: se genera directamente para el mes actual. Lista información del acreedor, la cuota, la amortización, el interés, el monto, el monto pendiente y el estado de pago.',
		group: 'dues',
		module: 'cat-5',
		Icon: FaCalendarAlt,
		formType: 'CuotasCreditos',
		hasFilters: false,
		defaults: { concept: 'all', dateFrom: '', dateTo: '', currency: 'all', query: 'all' },
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'currency', 'query'],
	},
	{
		id: 'Todas las cuotas de pasividades',
		title: 'Todas las cuotas de pasividades',
		subtitle: 'Historial completo de cuotas',
		description: 'Este reporte permite traer todas las cuotas de pasividades generadas en el sistema. Se puede filtrar por concepto, por estado de pago, por la fecha dentro de un rango de fechas y para una moneda específica. Lista información del acreedor, la cuota, la amortización, el interés, el monto, la fecha de vencimiento, el monto pendiente y el estado de pago.',
		group: 'dues',
		module: 'cat-5',
		Icon: FaListAlt,
		formType: 'CuotasCreditos',
		hasFilters: true,
		defaults: { concept: 'all', dateFrom: '', dateTo: '', currency: 'all', query: 'all' },
		urlKeys: ['concept', 'dateFrom', 'dateTo', 'currency', 'query'],
	},
	{
		id: 'Últimos pagos por cuota de deuda',
		title: 'Últimos pagos por cuota',
		subtitle: 'Último cobro de cada cuota, por cuenta',
		description: 'Este reporte permite traer, agrupado por cuenta, el último pago registrado de cada cuota de deuda. Se puede filtrar por cuenta y por la fecha dentro de un rango de fechas. Lista, para cada deudor y deuda, el detalle de las cuotas con la información de su último cobro.',
		group: 'dues',
		module: 'cat-3',
		Icon: FaHistory,
		formType: 'EstadoCuenta',
		hasFilters: true,
		defaults: { accountId: 'all', concept: 'all', dateFrom: '', dateTo: '', currency: 'all', query: 'all' },
		urlKeys: ['accountId', 'concept', 'dateFrom', 'dateTo', 'currency', 'query'],
	},
	{
		id: 'Estado Situación',
		title: 'Estado Situación',
		subtitle: 'Situación contable por cuenta',
		description: 'Este reporte permite traer el estado de situación contable agrupado por cuenta. Se puede filtrar por cuenta y por la fecha dentro de un rango de fechas. Lista, para cada cuenta, el detalle de sus deudas y los movimientos asociados, ofreciendo una visión consolidada de su situación.',
		group: 'consol',
		module: 'accent',
		Icon: FaBalanceScale,
		formType: 'EstadoCuenta',
		hasFilters: true,
		defaults: { accountId: 'all', concept: 'all', dateFrom: '', dateTo: '', currency: 'all', query: 'all' },
		urlKeys: ['accountId', 'concept', 'dateFrom', 'dateTo', 'currency', 'query'],
	},
];

export function getReport(id) {
	return REPORTS.find((r) => r.id === id);
}

function pick(source, keys) {
	const result = {};
	keys.forEach((key) => {
		if (source[key] !== undefined) result[key] = source[key];
	});
	return result;
}

export function seedFilterOptions(report, urlValues) {
	return {
		formType: report.formType,
		...pick(urlValues, report.urlKeys),
		page: urlValues.page || 1,
	};
}

export function clearFilterOptions(report) {
	return { formType: report.formType, ...report.defaults };
}
